import { useEffect, useRef, useState } from "react";
import type { Detail } from "./runbookTypes";
import { navigate, repoPath, useCurrentRepo } from "./router";
import { AdrLink } from "./AdrLink";
import { useApiResource } from "./useApiResource";

function checklistKey(id: string): string {
  return `runbook-checklist:${id}`;
}

function loadChecklist(id: string): Record<number, boolean> {
  try {
    const raw = window.localStorage.getItem(checklistKey(id));
    return raw ? (JSON.parse(raw) as Record<number, boolean>) : {};
  } catch {
    return {};
  }
}

// RunbookReader is the spec §6.4 runbook reader. Checklist state is scoped
// to Prerequisites — the one field the parser extracts structurally rather
// than infers (internal/index/operations) — and persists to localStorage
// ("Checklist state should initially remain local to the browser", spec
// §6.4), not applied to arbitrary rendered prose bullets, most of which
// aren't "steps" at all in the real corpus (e.g. a "Properties that
// matter" list is description, not a checklist).
//
// The server sanitises `html` before it reaches this component (see
// ADRReader's docstring for the full trust-boundary writeup — the same
// discipline applies here); dangerouslySetInnerHTML is safe only because of
// that upstream guarantee.
export function RunbookReader({ token, id }: { token: string; id: string }) {
  const repo = useCurrentRepo();
  const { status, data, message } = useApiResource<Detail>(token, repo, `/runbooks/${encodeURIComponent(id)}`);
  const [checklistMode, setChecklistMode] = useState(false);
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const contentRef = useRef<HTMLDivElement>(null);

  // Checklist state is per-runbook and only meaningful once a fetch for
  // this id has actually succeeded — reload it fresh whenever id changes
  // (a raw "on data change" effect would also fire for a same-id refetch
  // that happens to return the same object shape, which is fine here).
  useEffect(() => {
    if (status === "ready") {
      setChecked(loadChecklist(id));
    }
  }, [status, id]);

  // Copy buttons for command blocks (spec §6.4): injected onto the
  // server-rendered <pre><code> blocks after mount, since that content
  // isn't React-managed JSX.
  useEffect(() => {
    const root = contentRef.current;
    if (!root || status !== "ready") return;
    const blocks = root.querySelectorAll("pre");
    const cleanups: (() => void)[] = [];
    blocks.forEach((pre) => {
      if (pre.querySelector(".copy-button")) return;
      const code = pre.querySelector("code");
      const text = code?.textContent ?? pre.textContent ?? "";
      const button = document.createElement("button");
      button.type = "button";
      button.className = "copy-button";
      button.textContent = "Copy";
      const onClick = () => {
        void navigator.clipboard.writeText(text);
      };
      button.addEventListener("click", onClick);
      pre.appendChild(button);
      cleanups.push(() => button.removeEventListener("click", onClick));
    });
    return () => cleanups.forEach((fn) => fn());
  }, [status, data?.html]);

  function toggleChecked(i: number) {
    setChecked((prev) => {
      const next = { ...prev, [i]: !prev[i] };
      window.localStorage.setItem(checklistKey(id), JSON.stringify(next));
      return next;
    });
  }

  const backLink = (
    <a
      href={repoPath(repo, "runbooks")}
      onClick={(e) => {
        e.preventDefault();
        navigate(repoPath(repo, "runbooks"));
      }}
    >
      ← Back to runbooks
    </a>
  );

  if (status === "loading") {
    return (
      <section className="panel">
        {backLink}
        <p>Loading…</p>
      </section>
    );
  }
  if (status !== "ready" || !data) {
    return (
      <section className="panel">
        {backLink}
        <p className={status === "error" ? "err" : undefined}>{message}</p>
      </section>
    );
  }

  const toc = data.toc ?? [];
  const prerequisites = data.prerequisites ?? [];
  const relatedAdrs = data.relatedAdrs ?? [];

  return (
    <div className="doc-reader">
      <section className="panel doc-content">
        {backLink}
        <h2>{data.title}</h2>

        <div className={`runbook-banner${data.riskLevel === "high" ? " risk-high" : ""}`}>
          <div>
            <dt>Purpose</dt>
            <dd>
              {data.purpose || "—"}
              {data.purpose && data.purposeInferred ? " (inferred)" : ""}
            </dd>
          </div>
          <div>
            <dt>Environment</dt>
            <dd>
              {data.environment || "—"}
              {data.environment && data.environmentInferred ? " (inferred)" : ""}
            </dd>
          </div>
          <div>
            <dt>Risk level</dt>
            <dd>
              {data.riskLevel || "—"}
              {data.riskLevel && data.riskLevelInferred ? " (inferred)" : ""}
            </dd>
          </div>
          <div>
            <dt>Blast radius</dt>
            <dd>
              {data.blastRadius || "—"}
              {data.blastRadius && data.blastRadiusInferred ? " (inferred)" : ""}
            </dd>
          </div>
        </div>

        <h3>
          Prerequisites{" "}
          <button type="button" className="btn-ghost" onClick={() => setChecklistMode((v) => !v)}>
            {checklistMode ? "Exit checklist mode" : "Checklist mode"}
          </button>
        </h3>
        {prerequisites.length > 0 ? (
          <ul className={checklistMode ? "checklist" : undefined}>
            {prerequisites.map((p, i) => (
              <li key={i}>
                {checklistMode ? (
                  <label>
                    <input type="checkbox" checked={!!checked[i]} onChange={() => toggleChecked(i)} />
                    {p}
                  </label>
                ) : (
                  p
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None recorded.</p>
        )}

        <div ref={contentRef} className="rendered-markdown" dangerouslySetInnerHTML={{ __html: data.html }} />

        <h3>Related ADRs</h3>
        {relatedAdrs.length > 0 ? (
          <ul className="doc-toc-list">
            {relatedAdrs.map((a) => (
              <li key={a.id}>
                <AdrLink id={a.id} title={a.title} />{" "}
                {a.title || <span className="muted-note">(unresolved reference)</span>}
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted-note">None found.</p>
        )}

        <p>
          <a href={data.sourceUrl} target="_blank" rel="noopener noreferrer">
            View source on GitHub
          </a>
        </p>
      </section>

      {toc.length > 0 && (
        <nav className="doc-toc" aria-label="Table of contents">
          <h3>Contents</h3>
          <ul>
            {toc.map((h) => (
              <li key={h.id} className={h.level === 3 ? "toc-sub" : undefined}>
                <a href={`#${h.id}`}>{h.text}</a>
              </li>
            ))}
          </ul>
        </nav>
      )}
    </div>
  );
}
